import { useRef, useState } from "react";
import { Volume2, Mic, Star, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);

function similarity(target, said) {
  const t = norm(target);
  const s = new Set(norm(said));
  return t.filter((w) => s.has(w)).length / t.length;
}

function Stars({ n }) {
  return <div data-testid="repeat-stars" className="flex gap-1">{[1, 2, 3].map((k) => <Star key={k} size={32} className={k <= n ? "fill-[color:var(--sun)] text-[color:var(--sun)]" : "text-[color:var(--line)]"} />)}</div>;
}

function SentenceRow({ sentence, idx, story, narration }) {
  const [listening, setListening] = useState(false);
  const [heard, setHeard] = useState("");
  const [stars, setStars] = useState(0);
  const recRef = useRef(null);

  const record = async (n) => {
    setStars(n);
    try {
      await api.post("/progress", { type: "repeat", story_id: story.id, score: n, total: 3 });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const listen = () => {
    narration.stop();
    const rec = new SR();
    rec.lang = "fr-FR";
    rec.interimResults = false;
    const timer = setTimeout(() => rec.abort(), 8000);
    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setHeard(text);
      const sim = similarity(sentence, text);
      record(sim > 0.8 ? 3 : sim > 0.45 ? 2 : 1);
    };
    rec.onend = () => { clearTimeout(timer); setListening(false); };
    rec.onerror = () => {
      clearTimeout(timer);
      setListening(false);
      toast.error("Je n'ai pas bien entendu. Autorise le micro ou utilise « Je l'ai dit ! ».");
    };
    recRef.current = rec;
    setListening(true);
    try { rec.start(); } catch { clearTimeout(timer); setListening(false); }
  };

  return (
    <div data-testid={`repeat-row-${idx}`} className="rise card-paper p-6" style={{ animationDelay: `${idx * 80}ms` }}>
      <p className="font-story text-2xl leading-relaxed sm:text-3xl">« {sentence} »</p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button data-testid={`repeat-listen-${idx}`} onClick={() => narration.play(sentence)} className="btn-ghost"><Volume2 size={18} /> Écouter</button>
        {SR && (
          <button data-testid={`repeat-record-${idx}`} onClick={listen} disabled={listening}
            className={`btn-primary ${listening ? "pulse-ring" : ""}`}><Mic size={18} /> {listening ? "Je t'écoute…" : "À toi de dire !"}</button>
        )}
        <button data-testid={`repeat-selfcheck-${idx}`} onClick={() => record(3)} className={SR ? "btn-ghost" : "btn-primary"}><Check size={18} /> Je l'ai dit !</button>
        {stars > 0 && <Stars n={stars} />}
      </div>
      {heard && <p data-testid={`repeat-heard-${idx}`} className="mt-3 text-[color:var(--muted-ink)]">J'ai entendu : <em>« {heard} »</em></p>}
    </div>
  );
}

export default function Repeat({ story, narration }) {
  return (
    <div className="space-y-5">
      {!SR && <p data-testid="repeat-no-mic-note" className="rounded-2xl bg-[color:var(--paper-2)] px-4 py-3 text-sm font-semibold">La reconnaissance vocale n'est pas disponible sur ce navigateur (essayez Chrome). Votre enfant peut répéter à voix haute puis valider.</p>}
      {(story.repeat_sentences || []).map((s, i) => <SentenceRow key={i} sentence={s} idx={i} story={story} narration={narration} />)}
    </div>
  );
}
